import { Text } from '@components/v2/ui/Text';
import {
	ActionIcon,
	Checkbox,
	Combobox,
	Flex,
	Input,
	Pill,
	PillsInput,
	Tooltip,
	useCombobox,
	type ComboboxProps,
} from '@mantine/core';
import {
	IconChevronDown,
	IconX,
} from '@tabler/icons-react';
import { truncate } from 'lodash';
import {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import classes from './MultiSelect.module.css';

export type TOptionItem = {
	value: string;
	label: string;
};

export type TMultiSelectProps = {
	data: TOptionItem[];
	value: string[];
	onChange: (data: string[]) => void;
	placeholder?: string;
	clearable?: boolean;
	searchable?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	w?: string | number;
	comboboxProps?: Partial<ComboboxProps>;
};

export const MultiSelect = ({
	data,
	value,
	onChange,
	placeholder,
	clearable,
	searchable,
	size = 'md',
	w,
	comboboxProps,
}: TMultiSelectProps) => {
	const { t } = useTranslation();
	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
		onDropdownOpen: () =>
			combobox.updateSelectedOptionIndex('active'),
	});

	const [search, setSearch] = useState('');
	const searchRef = useRef<HTMLInputElement | null>(null);

	// 🔥 Autofocus sur l'input quand le dropdown s'ouvre
	useEffect(() => {
		if (combobox.dropdownOpened && searchable) {
			setTimeout(() => {
				searchRef.current?.focus();
			}, 0);
		}
	}, [combobox.dropdownOpened, searchable]);

	const handleValueSelect = (selectedValue: string) => {
		onChange(
			value.includes(selectedValue)
				? value.filter((v) => v !== selectedValue)
				: [...value, selectedValue]
		);
	};

	const handleValueRemove = (val: string) => {
		onChange(value.filter((v) => v !== val));
	};

	const filteredData = useMemo(() => {
		if (!searchable || !search.trim()) return data;
		const query = search.toLowerCase();

		return data.filter((item) => {
			const translated = t(item.label).toLowerCase();
			return (
				translated.includes(query) ||
				item.value.toLowerCase().includes(query)
			);
		});
	}, [data, search, searchable, t]);

	const selectedPills = value.map((selectedValue) => {
		const matchedItem = data.find(
			(item) => item.value === selectedValue
		);
		const label = matchedItem
			? matchedItem.label
			: selectedValue;

		return (
			<Tooltip
				key={selectedValue}
				disabled={t(label).length < 20}
				label={t(label)}
			>
				<Pill
					c="white"
					bg="var(--mantine-color-igluPrimary-9)"
					m="5px"
					withRemoveButton
					onRemove={() =>
						handleValueRemove(selectedValue)
					}
					pt="1px"
				>
					<Text lineClamp={1} variant="caption">
						{truncate(t(label), { length: 20 })}
					</Text>
				</Pill>
			</Tooltip>
		);
	});

	const options = filteredData.map((item) => (
		<Combobox.Option
			value={item.value}
			key={item.value}
			active={value.includes(item.value)}
			className={classes.option}
		>
			<Flex gap="sm" align="center">
				<Checkbox
					variant="outline"
					radius="5px"
					color="var(--mantine-color-igluPrimary-9)"
					checked={value.includes(item.value)}
					style={{ pointerEvents: 'none' }}
				/>
				<Text variant="body2">{t(item.label)}</Text>
			</Flex>
		</Combobox.Option>
	));

	return (
		<div style={{ width: w }}>
			<Pill.Group mb="xs">
				{selectedPills.length > 0 ? (
					selectedPills
				) : (
					<Input.Placeholder />
				)}
			</Pill.Group>

			<Combobox
				store={combobox}
				onOptionSubmit={handleValueSelect}
				withinPortal={false}
				{...comboboxProps}
				styles={{
					dropdown: { maxHeight: 200, overflowY: 'auto' },
				}}
			>
				<Combobox.DropdownTarget>
					<PillsInput
						rightSection={<IconChevronDown size={20} />}
						classNames={classes}
						pointer
						size={size}
						onClick={() => combobox.toggleDropdown()}
					>
						<Text color="var(--mantine-color-igluSecondary-9)">
							{placeholder}
						</Text>

						{clearable && value.length > 0 && (
							<ActionIcon
								color="var(--mantine-color-igluBorder-9)"
								radius="48px"
								variant="transparent"
								size="18px"
								onClick={() => onChange([])}
							>
								<IconX
									color="var(--mantine-color-igluPrimary-9)"
									size="18px"
								/>
							</ActionIcon>
						)}
					</PillsInput>
				</Combobox.DropdownTarget>

				<Combobox.Dropdown>
					{searchable && (
						<Combobox.Search
							ref={searchRef}
							placeholder={t('global.search')}
							value={search}
							onChange={(event) =>
								setSearch(event.currentTarget.value)
							}
							style={{ marginBottom: '8px' }}
						/>
					)}
					<Combobox.Options>
						{options.length > 0 ? (
							options
						) : (
							<Combobox.Empty>
								{t('global.noData')}
							</Combobox.Empty>
						)}
					</Combobox.Options>
				</Combobox.Dropdown>
			</Combobox>
		</div>
	);
};
